import {
  ApplicationRef,
  Component,
  ComponentRef,
  ElementRef,
  EnvironmentInjector,
  NgZone,
  OnDestroy,
  Type,
  createComponent,
  inject,
  signal,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {BoosterStateService} from '@services/card-game/booster-state.service';
import {CardSoundService} from '@services/card-game/card-sound.service';
import {CARD_GAP, CARD_H, CARD_W} from '@/app/core/models/collection/booster.constants';
import {CardRarity} from '@/app/shared/components/card/card.model';
import {DrawnCard} from '@/app/core/models/collection/drawn-card.model';
import {CommonCardComponent}    from '@/app/shared/components/card/rarities/common-card.component';
import {RareCardComponent}      from '@/app/shared/components/card/rarities/rare-card.component';
import {EpicCardComponent}      from '@/app/shared/components/card/rarities/epic-card.component';
import {LegendaryCardComponent} from '@/app/shared/components/card/rarities/legendary-card.component';
import {CollectorCardComponent} from '@/app/shared/components/card/rarities/collector-card.component';

/** Avance du son sur l'apparition visuelle de la carte, pour compenser le temps de démarrage de `audio.play()`. */
const SOUND_LEAD_MS = 90;

/** Durée du flip GSAP dos → recto dans CardComponent (onClick). */
const FLIP_DURATION_MS = 550;

/** Associe chaque rareté à son composant Angular dédié. */
const RARITY_COMPONENT: Record<CardRarity, Type<unknown>> = {
	COMMON:    CommonCardComponent,
	RARE:      RareCardComponent,
	EPIC:      EpicCardComponent,
	LEGENDARY: LegendaryCardComponent,
	COLLECTOR: CollectorCardComponent,
};

@Component({
	selector: 'peps-cards-layer',
	standalone: true,
	templateUrl: 'cards-layer.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styles: [`
    div {
      position: absolute;
      inset: 0;
      z-index: 2;
      pointer-events: none;
    }
  `],
})
export class CardsLayerComponent implements OnDestroy {
	private readonly layerRef     = viewChild.required<ElementRef<HTMLDivElement>>('layer');
	private readonly stateService = inject(BoosterStateService);
	private readonly appRef       = inject(ApplicationRef);
	private readonly envInjector  = inject(EnvironmentInjector);
	private readonly soundService = inject(CardSoundService);
	private readonly ngZone       = inject(NgZone);

	/**
	 * Position finale de chaque carte (décalage du centre de l'écran + échelle), publiée une fois les
	 * cartes posées : permet au parent d'ancrer des éléments (badge « possédé », bouton décraft) dessous.
	 */
	readonly slots = signal<{x: number; y: number; scale: number}[]>([]);

	/** Indices des cartes retournées (recto visible) par l'utilisateur — les overlays du parent en dépendent. */
	readonly revealed = signal<ReadonlySet<number>>(new Set());

	private cardEls: HTMLElement[] = [];
	private cardRefs: ComponentRef<unknown>[] = [];
	private cascadeStarted = false;
	private idleAnimations: Animation[] = [];
	private markDoneTimer: ReturnType<typeof setTimeout> | null = null;
	private hoverCleanups: (() => void)[] = [];
	private settledScale = 1;
	/** Incrémenté à chaque `build()`/`reset()` — annule une création étalée sur plusieurs frames si périmée. */
	private buildToken = 0;

	/**
	 * Crée un composant de rareté par carte et les positionne au centre.
	 * Étalé sur une frame par carte (au lieu d'une boucle synchrone) pour ne
	 * pas cumuler N `createComponent()` + `detectChanges()` d'un coup pile
	 * quand la vidéo d'ouverture démarre. `launchCascade()` n'est de toute
	 * façon appelée que plusieurs secondes plus tard (fin de vidéo), largement
	 * le temps que tout soit prêt.
	 */
	build(): void {
		this.reset();
		const layer = this.layerRef().nativeElement;
		const cards = this.stateService.cards();
		const token = this.buildToken;

		// Pur DOM/Angular-view-manuel, aucun binding template n'en dépend :
		// hors zone pour ne pas déclencher de change-detection app entière.
		this.ngZone.runOutsideAngular(() => {
			const spawnNext = (index: number): void => {
				if (token !== this.buildToken || index >= cards.length) return;
				this.spawnCard(cards[index], layer);
				requestAnimationFrame(() => spawnNext(index + 1));
			};
			spawnNext(0);
		});
	}

	/** Instancie et positionne (invisible, scale 0) le composant d'une carte tirée. */
	private spawnCard(drawnCard: DrawnCard, layer: HTMLElement): void {
		const componentClass = RARITY_COMPONENT[drawnCard.card.rarity as CardRarity] ?? CommonCardComponent;
		const ref = createComponent(componentClass as Type<{ card: unknown; score: unknown; initiallyFlipped: boolean }>, {
			environmentInjector: this.envInjector,
		});
		ref.setInput('card', drawnCard.card);
		ref.setInput('score', drawnCard.score);
		ref.setInput('initiallyFlipped', true);
		// Le hover/zoom est déjà géré par enableHoverZoom() ci-dessous : on désactive le
		// tilt souris + halo interne de CardComponent pour éviter deux systèmes concurrents
		// sur le même élément (et réduire le setup GSAP inutile, voir FIX 4).
		ref.setInput('interactive', false);
		this.appRef.attachView(ref.hostView);
		ref.changeDetectorRef.detectChanges();

		const el = ref.location.nativeElement as HTMLElement;
		el.style.cssText = `
			position: absolute;
			top: 50%;
			left: 50%;
			margin-left: -${CARD_W / 2}px;
			margin-top: -${CARD_H / 2}px;
			opacity: 0;
			translate: 0px 0px;
			scale: 0;
			will-change: translate, scale;
			pointer-events: none;
		`;
		layer.appendChild(el);

		this.cardEls.push(el);
		this.cardRefs.push(ref);
	}

	/**
	 * Déploie les cartes en cascade vers leur position finale.
	 * Tout est pur DOM/CSS (aucun binding template) : sorti de la zone
	 * Angular pour que les `setTimeout`/`requestAnimationFrame` en cascade ne
	 * redéclenchent pas de change-detection app entière à chaque étape,
	 * pile pendant que la vidéo tourne. Seul `markDone()` (lu par le template
	 * pour afficher le bouton fermer) rentre explicitement dans la zone.
	 */
	launchCascade(): void {
		if (this.cascadeStarted) return;
		this.cascadeStarted = true;
		const count = this.cardEls.length;
		const scale = this.getResponsiveScale(count);

		this.ngZone.runOutsideAngular(() => {
			this.cardEls.forEach((el, i) => {
				const {x, y} = this.getCardFinalPos(i, count, scale);
				setTimeout(() => {
					// Le son démarre en premier : `audio.play()` a un léger temps de
					// démarrage incompressible (décodage MP3, etc.) avant d'être audible.
					// Sans ce petit décalage, la carte apparaît visuellement avant que
					// le son ne se fasse réellement entendre. SOUND_LEAD_MS absorbe ce
					// délai pour que carte et son atterrissent perçus comme synchrones.
					this.soundService.playCardReveal();
					setTimeout(() => {
						el.style.transition = 'none';
						el.style.opacity = '1';
						el.style.translate = '0px 0px';
						el.style.scale = '0';
						requestAnimationFrame(() =>
							requestAnimationFrame(() => {
								el.style.transition = [
									'translate 0.65s cubic-bezier(0.34,1.56,0.64,1)',
									'scale 0.65s cubic-bezier(0.34,1.56,0.64,1)',
									'opacity 0.25s ease',
								].join(', ');
								el.style.translate = `${x}px ${y}px`;
								el.style.scale = `${scale}`;
								if (i === count - 1) {
									setTimeout(() => {
										this.settledScale = scale;
										this.ngZone.run(() => this.slots.set(
											this.cardEls.map((_, idx) => ({...this.getCardFinalPos(idx, count, scale), scale}))
										));
										this.cardEls.forEach(e => {
											e.style.pointerEvents = 'auto';
											e.style.transition = 'scale 0.3s cubic-bezier(0.34,1.56,0.64,1)';
											// translate ne bouge plus après le settle (seul scale change encore, au hover) —
											// libère un calque compositor devenu inutile.
											e.style.willChange = 'scale';
										});
										this.enableHoverZoom();
										this.startIdleAnim();
									}, 700);
								}
							})
						);
					}, SOUND_LEAD_MS);
				}, i * 400);
			});

			this.markDoneTimer = setTimeout(
				() => this.ngZone.run(() => this.stateService.markDone()),
				count * 500 + 1000,
			);
		});
	}

	/** Détruit tous les composants et vide le layer. */
	reset(): void {
		this.buildToken++; // annule une éventuelle création de cartes encore étalée sur plusieurs frames
		if (this.markDoneTimer !== null) {
			clearTimeout(this.markDoneTimer);
			this.markDoneTimer = null;
		}
		this.stopIdleAnim();
		this.disableHoverZoom();
		this.cascadeStarted = false;
		this.slots.set([]);
		this.revealed.set(new Set());
		this.cardEls = [];

		for (const ref of this.cardRefs) {
			this.appRef.detachView(ref.hostView);
			ref.destroy();
		}
		this.cardRefs = [];
		this.layerRef().nativeElement.innerHTML = '';
	}

	/** Réduit la taille des cartes pour que la rangée tienne dans la largeur d'écran. */
	private getResponsiveScale(count: number): number {
		const sidePadding = 24;
		const naturalW = count * CARD_W + (count - 1) * CARD_GAP;
		const available = Math.max(window.innerWidth - sidePadding * 2, 0);
		return Math.min(1, available / naturalW);
	}

	private getCardFinalPos(i: number, count: number, scale: number): {x: number; y: number} {
		const cardW = CARD_W * scale;
		const gap = CARD_GAP * scale;
		const totalW = count * cardW + (count - 1) * gap;
		const startX = (window.innerWidth - totalW) / 2;
		return {
			x: startX + i * (cardW + gap) - window.innerWidth / 2 + cardW / 2,
			y: window.innerHeight * 0.05,
		};
	}

	private startIdleAnim(): void {
		const count = this.cardEls.length;
		this.idleAnimations = this.cardEls.map((el, i) => {
			const phase    = (i / count) * 2 * Math.PI;
			const delay    = -(phase / (2 * Math.PI)) * 2500;
			return el.animate(
				[
					{ transform: 'translateY(0px)' },
					{ transform: 'translateY(-6px)' },
					{ transform: 'translateY(0px)' },
				],
				{ duration: 2500, iterations: Infinity, easing: 'ease-in-out', delay },
			);
		});
	}

	private stopIdleAnim(): void {
		this.idleAnimations.forEach(a => a.cancel());
		this.idleAnimations = [];
	}

	/** Grossit la carte survolée et la fait passer au premier plan. */
	private enableHoverZoom(): void {
		this.cardEls.forEach((el, i) => {
			const onEnter = () => {
				el.style.scale = `${this.settledScale * 1.18}`;
				el.style.zIndex = '10';
			};
			const onLeave = () => {
				el.style.scale = `${this.settledScale}`;
				el.style.zIndex = `${i}`;
			};
			// Le 1er clic déclenche le flip dos → recto de CardComponent (0.5 s) : on publie la
			// révélation une fois l'animation finie.
			const onFirstClick = () => {
				setTimeout(
					() => this.ngZone.run(() => this.revealed.update(set => new Set(set).add(i))),
					FLIP_DURATION_MS,
				);
			};
			el.addEventListener('mouseenter', onEnter);
			el.addEventListener('mouseleave', onLeave);
			el.addEventListener('click', onFirstClick, {once: true});
			this.hoverCleanups.push(() => {
				el.removeEventListener('mouseenter', onEnter);
				el.removeEventListener('mouseleave', onLeave);
				el.removeEventListener('click', onFirstClick);
			});
		});
	}

	private disableHoverZoom(): void {
		this.hoverCleanups.forEach(fn => fn());
		this.hoverCleanups = [];
	}

	ngOnDestroy(): void {
		if (this.markDoneTimer !== null) clearTimeout(this.markDoneTimer);
		this.stopIdleAnim();
		this.disableHoverZoom();
		for (const ref of this.cardRefs) {
			this.appRef.detachView(ref.hostView);
			ref.destroy();
		}
	}
}
